import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, shareReplay } from 'rxjs';
import { ThirdPartyLicenseEntry, ThirdPartyLicenses, ThirdPartyPackage } from '../models/third-party-licenses.model';

const LICENSES_URL = 'assets/licenses/third-party-licenses.json';

/**
 * Loads the build-time generated attribution artifact and flattens it into a list
 * ready to render.
 *
 * Only the data loading lives here, deliberately: the presentation differs per app
 * (Ionic in the mobile apps, Syncfusion in coach-portal) and this lib is consumed by
 * every app, so importing @ionic/angular here would pull Ionic and Capacitor into
 * authority's and coach-portal's Nx closures -- which would then corrupt their own
 * generated license artifacts, since the generator derives app scope from that graph.
 *
 * The URL is relative so it resolves against the document base, which keeps it
 * working from the Capacitor filesystem on device as well as from a web origin.
 */
@Injectable({ providedIn: 'root' })
export class ThirdPartyLicensesService {
  private readonly http = inject(HttpClient);

  /** Packages first, then manual entries; each already paired with its license text(s). */
  readonly entries$: Observable<ThirdPartyLicenseEntry[]> = this.http.get<ThirdPartyLicenses>(LICENSES_URL).pipe(
    map((artifact) => {
      const resolve = (pkg: ThirdPartyPackage): ThirdPartyLicenseEntry => {
        const texts = pkg.licenseTextIds.map((id) => artifact.licenseTexts[id]).filter(Boolean);
        return {
          ...pkg,
          licenseTexts: texts.map((text) => text.content).filter(Boolean),
          notices: texts.flatMap((text) => text.notices ?? []),
        };
      };
      return [...artifact.packages.map(resolve), ...artifact.manual.map(resolve)];
    }),
    shareReplay({ bufferSize: 1, refCount: false }),
  );
}
